import { useState } from "react";
import { api } from "../data/api";

function getInitials(name, email) {
  if (name && name.trim()) {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (email || "?")[0].toUpperCase();
}

const AVATAR_COLORS = {
  admin:      { bg: "#0798f8", text: "#93c5fd" },
  superuser:  { bg: "#4c1d95", text: "#c4b5fd" },
  user:       { bg: "#14532d", text: "#86efac" },
  promo:      { bg: "#7c2d12", text: "#fdba74" },
  custom:     { bg: "#f3670a", text: "#d6d3d1" },
  affiliate:  { bg: "#f3a909", text: "#6ee7b7" },
};

const ROLE_LABELS = {
  admin:     "Administrator",
  superuser: "Super User",
  user:      "User",
  promo:     "Promo Uploader",
  custom:    "Custom Access",
  affiliate: "Affiliate",
};

export default function UserProfile({ user, onUpdated }) {
  const [name, setName]     = useState(user?.name || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);

  const colors = AVATAR_COLORS[user?.role] || AVATAR_COLORS.custom;
  const initials = getInitials(user?.name, user?.email);

  const handleSave = async () => {
    if (!name.trim()) { setError("Name cannot be empty"); return; }
    setSaving(true); setError(null);
    try {
      await api.profile.updateName(name.trim());
      setSuccess(true);
      setEditing(false);
      setTimeout(() => setSuccess(false), 3000);
      if (onUpdated) onUpdated({ ...user, name: name.trim() });
    } catch (e) {
      setError(e.message || "Failed to update name");
    } finally { setSaving(false); }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: 600 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 20 }}>Profile</h2>

      {/* Avatar card */}
      <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "20px 24px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, marginBottom: 24 }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: colors.bg, color: colors.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, flexShrink: 0, letterSpacing: "-0.02em" }}>
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>{user?.name || "—"}</div>
          <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 2 }}>{user?.email}</div>
          <div style={{ marginTop: 6 }}>
            <span style={{ display: "inline-block", background: colors.bg, color: colors.text, borderRadius: 5, padding: "2px 9px", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase" }}>
              {ROLE_LABELS[user?.role] || user?.role}
            </span>
          </div>
        </div>
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editing ? 10 : 0 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>Display Name</div>
              {!editing && <div style={{ fontSize: 13, color: "var(--ink)" }}>{user?.name || <span style={{ color: "var(--text-dim)" }}>Not set</span>}</div>}
            </div>
            {!editing && (
              <button onClick={() => { setEditing(true); setError(null); setSuccess(false); }} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
                Edit
              </button>
            )}
          </div>
          {editing && (
            <div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") { setEditing(false); setName(user?.name || ""); } }}
                autoFocus
                style={{ width: "100%", boxSizing: "border-box", background: "var(--bg)", border: "1px solid var(--border-strong)", borderRadius: 6, padding: "8px 10px", fontSize: 13, color: "var(--text)", fontFamily: "inherit", outline: "none", marginBottom: 8 }}
              />
              {error && <div style={{ fontSize: 12, color: "var(--red, #dc2626)", marginBottom: 8 }}>{error}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleSave} disabled={saving} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "6px 16px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, fontFamily: "inherit" }}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={() => { setEditing(false); setName(user?.name || ""); setError(null); }} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 14px", fontSize: 12, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {success && <div style={{ fontSize: 12, color: "var(--green, #16a34a)", marginTop: 8 }}>✓ Name updated</div>}
        </div>

        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>Email Address</div>
          <div style={{ fontSize: 13, color: "var(--ink)" }}>{user?.email}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 3 }}>{user?.role === "superuser" ? "" : "Email cannot be changed. Contact your Super User to update it."}</div>
        </div>

        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>Role</div>
          <div style={{ fontSize: 13, color: "var(--ink)" }}>{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>

        {user?.role === "custom" && user?.permissions && (
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Page Access</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {Object.entries(user.permissions)
                .filter(([, lvl]) => lvl !== "none")
                .map(([page, lvl]) => (
                  <span key={page} style={{ background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 5, padding: "3px 8px", fontSize: 11, color: "var(--text-sub)", textTransform: "capitalize" }}>
                    {page}: <strong>{lvl}</strong>
                  </span>
                ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
